import * as vp from 'vopay';
import * as Yup from 'yup';
const { TextField, Select } = vp;
import { FieldData } from '../../../../Form/types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

type AccountTranferRowsProp = {
  MainAccountName: string;
  MainAccountId: string;
  MainAccountBalance: string;
  fromSubAccounts: vp.SubAccountsLists[];
  toSubAccounts: vp.SubAccountsLists[];
  country: string;
  onSearchFromSubAccounts: (searchText: string) => void;
  onSearchToSubAccounts: (searchText: string) => void;
  fetchFromSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
  fetchToSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
};

export type AccountTransferRequest = {
  Amount: string;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
};
export type AccountTransferSerializedRequest = {
  Amount: number;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
};

export const buildAccountTransferRows = ({
  MainAccountName,
  MainAccountBalance,
  MainAccountId,
  fromSubAccounts,
  toSubAccounts,

  country,
  onSearchFromSubAccounts,
  onSearchToSubAccounts,
  fetchFromSubAccountsAsync,
  fetchToSubAccountsAsync,
}: AccountTranferRowsProp): FieldData<AccountTransferRequest>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.AccountTransfer' });
  const [fromBankLabel, setFromBankLabel] = useState('');
  const [toBankLabel, setToBankLabel] = useState('');

  const CurrencyOptions = {
    ...vp.CURRENCY_LIST_OPTIONS,
  };

  return [
    {
      label: () => t('Currency.label'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            options={country === 'US' ? vp.convertToOptions(vp.US_CURRENCY_ONLY) : vp.convertToOptions(CurrencyOptions)}
            placeholder={t('Currency.placeholder')}
            {...fieldData}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('From.label'),
      fieldName: 'DebitorAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const FromBankAccountsOptions = buildSubAccountOptions(fromSubAccounts, ctx.values.Currency, false);
        return (
          <Select
            version="2"
            options={FromBankAccountsOptions}
            placeholder={t('From.placeholder')}
            sort={false}
            filterable
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => onSearchFromSubAccounts(e.filter), 300);
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;
              return <>{fromBankLabel || value}</>;
            }}
            {...fieldData}
            onChange={async (e, { value, label }) => {
              fieldData.onChange(e);
              if (!value) return;
              if (value === MainAccountId) {
                setFromBankLabel(`${MainAccountName} ($${vp.formattedCurrency(MainAccountBalance || '0.00')})`);
                return;
              }
              const res = await fetchFromSubAccountsAsync({ ActiveOnly: true, Limit: 1, SubaccountID: value });
              const found = res.Subaccounts.find((a) => a.AccountID === value);
              if (!found) {
                setFromBankLabel(label);
                return;
              }
              const balance =
                Object.values(found.Balances).find((b) => b.Currency === ctx.values.Currency)?.AvailableBalance ||
                '0.00';
              setFromBankLabel(`${found.LegalBusinessName} ($${vp.formattedCurrency(balance)})`);
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('To.label'),
      fieldName: 'RecipientAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const ToBankAccountsOptions = buildSubAccountOptions(
          toSubAccounts.filter((data) => ctx.values.DebitorAccountID !== data.AccountID),
          ctx.values.Currency,
          ctx.values.DebitorAccountID === MainAccountId,
        );
        return (
          <Select
            version="2"
            options={ToBankAccountsOptions}
            placeholder={t('To.placeholder')}
            sort={false}
            filterable
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => onSearchToSubAccounts(e.filter), 300);
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;
              return <>{toBankLabel || value}</>;
            }}
            {...fieldData}
            onChange={async (e, { value, label }) => {
              fieldData.onChange(e);
              if (!value) return;
              if (value === MainAccountId) {
                setToBankLabel(`${MainAccountName} ($${vp.formattedCurrency(MainAccountBalance || '0.00')})`);
                return;
              }
              const res = await fetchToSubAccountsAsync({ ActiveOnly: true, Limit: 1, SubaccountID: value });
              const found = res.Subaccounts.find((a) => a.AccountID === value);
              if (!found) {
                setToBankLabel(label);
                return;
              }
              const balance =
                Object.values(found.Balances).find((b) => b.Currency === ctx.values.Currency)?.AvailableBalance ||
                '0.00';
              setToBankLabel(`${found.LegalBusinessName} ($${vp.formattedCurrency(balance)})`);
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      serializeField: (value) => Number(vp.sanitizeToNumeric(value.Amount)),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField preset="currency" {...fieldData} variant="outlineShadow" />;
      },
      validate: (ctx) => {
        const availableBalance =
          ctx.values.DebitorAccountID == MainAccountId
            ? MainAccountBalance
            : Object.values(
                fromSubAccounts.find((data: vp.SubAccountsLists) => data.AccountID === ctx.values.DebitorAccountID)
                  ?.Balances || {},
              ).find((balance: vp.SubAccountsLists['Balances'][number]) => balance.Currency === ctx.values.Currency)
                ?.AvailableBalance;

        return Yup.string()
          .test('amount-validation', t('Validation.minimumAmount'), (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .test(
            'amount-validation',
            t('Validation.insufficientFunds', { maxAmount: vp.formattedCurrency(String(availableBalance)) }),
            (value) => {
              const availableFundsValidation =
                Number(vp.sanitizeToNumeric(value)) > Number(vp.sanitizeToNumeric(availableBalance));
              return !availableFundsValidation;
            },
          )
          .required();
      },
      showField: () => true,
    },
  ];
  function buildSubAccountOptions(
    lists: vp.SubAccountsLists[],
    currencySelected: string,
    removeMain: boolean,
  ): { label: string; value: string }[] {
    const initialOption = removeMain
      ? []
      : [
          {
            label: `${MainAccountName} ($${vp.formattedCurrency(MainAccountBalance || '0.00')})`,
            value: MainAccountId,
          },
        ];

    return [
      ...initialOption,
      ...lists
        .sort((a, b) => a.LegalBusinessName.localeCompare(b.LegalBusinessName))
        .map((data) => ({
          value: data.AccountID,
          label: `${data.LegalBusinessName} ($${vp.formattedCurrency(
            Object.values(data.Balances).find((balance) => balance.Currency === currencySelected)?.AvailableBalance ||
              '0.00',
          )})`,
        })),
    ];
  }
};
